import { useEffect, useState } from "react";
import { hivesApi } from "../../services/hivesApi";
import { useToast } from "../../context/ToastContext";
import { Modal } from "../common/Modal";
import { Button } from "../common/Button";
import { Field } from "../common/Field";

/* The column's own vocabulary, not a longer or friendlier one.
   CK_HiveLoss_cause rejects anything else, so offering a sixth option
   here would be a choice the database refuses only after Save. */
const CAUSES = ["Winter", "Varroa", "Foulbrood", "Queen", "Other"];

/**
 * A colony lost, recorded as a regulatory filing (#432).
 *
 * ── WHY THIS EXISTS AT ALL ──
 *
 * app.HiveLossReports has been read by the Loss report since V012 and
 * written by NOTHING. scripts/tables_nothing_writes.py found it beside
 * three others, and of the four this is the one whose silence says the
 * most: AN EMPTY LOSS REPORT IS INDISTINGUISHABLE FROM A SEASON WITH NO
 * LOSSES, and that is a document an inspector may be handed.
 *
 * ── WHY IT IS NOT DERIVED FROM THE ARCHIVE REASON (#428) ──
 *
 * Archiving already asks why, and folding the two together was the
 * tempting shortcut. The vocabularies do not line up:
 *
 *     archive_reason  Collapsed, Combined with another hive, Disease,
 *                     Queenless, Absconded, Sold, Died out, Other
 *     cause           Winter, Varroa, Foulbrood, Queen, Other
 *
 * "Disease" does not say whether it was Varroa or Foulbrood. "Sold" and
 * "Combined with another hive" are not losses at all. Deriving one from
 * the other would invent precision nobody supplied and file it.
 *
 * `reported_to_agency` settles it: whether you told the agency is a fact
 * about what YOU did, and no archive action can imply it.
 *
 * ── AND IT DOES NOT ARCHIVE THE HIVE ──
 *
 * Deliberately. A keeper may record a loss on a hive they are about to
 * restock, and archiving it for them would take the colony off their
 * list without being asked. Archiving stays its own decision, with its
 * own reason, one button along.
 */
export function RecordLossDialog({ open, hive, onClose, onRecorded }) {
  const { notify } = useToast();
  const [cause, setCause] = useState("");
  const [lossDate, setLossDate] = useState("");
  const [reported, setReported] = useState(false);
  const [reportDate, setReportDate] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  // Cleared on OPEN rather than on close, so a dialog reopened after a
  // mistake does not start with the previous answer already filled in.
  useEffect(() => {
    if (open) {
      setCause(""); setLossDate(""); setReported(false);
      setReportDate(""); setNotes(""); setBusy(false);
    }
  }, [open]);

  async function submit() {
    // The cause is the only required field. The date defaults to today
    // server-side, and a keeper recording a loss the evening they found
    // it should not have to type a date to say so.
    if (!cause) return notify("What was the cause?", "warning");
    setBusy(true);
    try {
      await hivesApi.recordLoss(hive.hive_id, {
        cause,
        loss_date: lossDate || undefined,
        reported_to_agency: reported,
        report_date: reported ? (reportDate || undefined) : undefined,
        notes: notes.trim() || undefined,
      });
      notify("Loss recorded", "success");
      onRecorded?.();
      onClose();
    } catch (err) {
      notify(err.apiMessage || "Could not record that loss", "danger");
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Record a loss — hive ${hive?.hive_number ?? ""}`}
      footer={<Button onClick={submit} disabled={busy || !cause}>Record loss</Button>}
    >
      <div style={{ display: "grid", gap: "var(--space-3)" }}>
        <p style={{ margin: 0, color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
          This goes on your Loss report, which is the one a bee inspector may
          ask for. It does not archive the hive.
        </p>

        <Field label="Cause" hint="The categories the loss report uses.">
          {(id, aria) => (
            <select id={id} {...aria} className="touch-target" value={cause}
                    onChange={(e) => setCause(e.target.value)}>
              <option value="">Choose a cause…</option>
              {CAUSES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
        </Field>

        <Field label="When" hint="Leave blank for today.">
          {(id, aria) => (
            <input id={id} {...aria} type="date" className="touch-target"
                   value={lossDate} onChange={(e) => setLossDate(e.target.value)} />
          )}
        </Field>

        <label style={{ display: "flex", gap: "var(--space-2)", alignItems: "center" }}>
          <input id="record-loss-dialog-reported" name="reported" type="checkbox" checked={reported}
                 onChange={(e) => setReported(e.target.checked)} />
          I have reported this to the agency
        </label>

        {/* ONLY WHEN IT WAS REPORTED. A filing date sitting beside an
            unticked box is a date for a report nobody made, and this row
            is evidence -- the endpoint drops it for the same reason. */}
        {reported && (
          <Field label="Reported on" hint="When you told them.">
            {(id, aria) => (
              <input id={id} {...aria} type="date" className="touch-target"
                     value={reportDate} onChange={(e) => setReportDate(e.target.value)} />
            )}
          </Field>
        )}

        <Field label="Notes" hint="Anything the categories do not cover.">
          {(id, aria) => (
            <textarea id={id} {...aria} className="touch-target" rows={3}
                      // 1000, matching app.HiveLossReports.notes.
                      maxLength={1000} value={notes}
                      onChange={(e) => setNotes(e.target.value)} />
          )}
        </Field>
      </div>
    </Modal>
  );
}
